import React, { useState, useEffect } from 'react'

import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import Card from '@/shared/Card'
import SVGIcon from '@/components/icon'
import StatusPill from '@/shared/StatusPill'
import { FilmIcon } from '@heroicons/react/solid'

const talentKindHash = {
  'voice_actor': { image: 'voiceactor' },
  'live_actor': { image: 'actor' },
  'singer': { image: 'singer' },
  'artist': { image: 'artist' },
  'animator': { image: 'animator' },
  'video_editor': { image: 'video-editor' },
  'music_composer': { image: 'musiccomposer' },
  'audio_engineer': { image: 'audio-engineer' },
  'writer': { image: 'writer' },
  'director': { image: 'director' },
  'agent': { image: 'agent' },
  'producer': { image: 'producer' }
}

const TYPE_FILTERS = [
  { key: null, label: 'All', advancedUrl: null },
  { key: 'User', label: 'Members', advancedUrl: (q) => `/talent?page=1&contains=${encodeURIComponent(q)}&search=is:all&` },
  { key: 'Project', label: 'Projects', advancedUrl: (q) => `/?page=1&payment=all&classification=all&order_by=magic&search=is:all|co:${encodeURIComponent(q)}&` },
  { key: 'Credit', label: 'Credits', advancedUrl: (q) => `/credits/search?search=${encodeURIComponent(q)}` }
]

// --- User Row ---

const UserRow = ({ result }) => {
  const { object } = result
  const metas = object.metas ? object.metas.split(' ').filter(Boolean) : []

  return (
    <li className="bg-gray-50 dark:bg-gray-800 hover:bg-cccblue hover:bg-opacity-5 dark:hover:bg-gray-900 p-2 shadow sm:p-2 sm:rounded-lg">
      <div>
        <div className="flex gap-x-2 sm:justify-between sm:items-center">
          <div className="flex-shrink-0">
            <a href={`/m/${object.username}`}>
              <img className='h-16 w-16 rounded-sm' src={object.imageUrl} alt={object.displayName} />
            </a>
          </div>
          <div className="flex-1 px-0 md:px-4 md:py-0 pb-2 w-full truncate">
            <div className='flex flex-col truncate'>
              <span className="flex space-x-2">
                <a className="font-medium" href={`/m/${object.username}`}>{object.displayName}</a>
                <span className='text-xs dark:text-gray-300 text-gray-500 flex items-center'>@{object.username}</span>
              </span>
              {object.headline && <div className="text-sm dark:text-gray-300 text-gray-800 truncate" dangerouslySetInnerHTML={{ __html: object.headline }} />}
            </div>
            {metas.length > 0 && (
              <div className='pt-1 flex justify-start items-center space-x-1 text-xs whitespace-nowrap truncate'>
                {metas.map((meta) => (
                  <StatusPill key={meta} status='default' text={meta.replace('is:', '').replace(/_/g, ' ')} />
                ))}
              </div>
            )}
            <div className='flex flex-col sm:flex-row justify-start items-start sm:items-center sm:space-x-3 mt-2 text-gray-900 font-medium text-sm dark:text-gray-100'>
              <a href={`/m/${object.username}`} className='whitespace-nowrap flex items-center space-x-1 text-gray-700 dark:text-gray-400'>
                <SVGIcon name='icon-recommendation' className='h-5 w-5' />
                <span><span className='text-green-500'>{object.recommendationsCount || 0}</span> Recs</span>
              </a>
              <a href={`/m/${object.username}`} className='whitespace-nowrap flex items-center space-x-1 text-gray-700 dark:text-gray-400'>
                <SVGIcon name='icon-submissions' className='h-5 w-5' />
                <span><span className='text-green-500'>{object.auditionsCount || 0}</span> submissions</span>
              </a>
              <a href={`/m/${object.username}`} className='whitespace-nowrap flex items-center space-x-1 text-gray-700 dark:text-gray-400'>
                <SVGIcon name='icon-credits' className='h-5 w-5' />
                <span><span className='text-green-500'>{object.userCreditsCount || 0}</span> credits</span>
              </a>
              <a href={`/m/${object.username}`} className='whitespace-nowrap flex items-center space-x-1 text-gray-700 dark:text-gray-400'>
                <SVGIcon name='icon-followers' className='h-5 w-5' />
                <span><span className='text-green-500'>{object.followeesCount || 0}</span> followers</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}

// --- Project + Role Rows ---

const ProjectRoleRow = ({ role, bgColor }) => {
  const kind = talentKindHash[role.isA] || { image: 'director' }

  return (
    <div className='my-1 px-4 py-1'>
      <div className="flex items-start space-x-3">
        <div className="relative m-1">
          <img className={`h-10 w-10 rounded-sm bg-gray-200 ring-4 ring-${bgColor}`} src={role.imageUrl} alt={role.name} />
          <span className={`absolute -bottom-0.5 -right-1 bg-${bgColor} text-white rounded-tl px-0.5 py-px`}>
            <SVGIcon name={kind.image} className='h-5 w-5' />
          </span>
        </div>
        <div className="flex-1 truncate">
          <div className='flex flex-col sm:flex-row justify-start gap-x-2 sm:items-center'>
            <span className={`px-2 text-xs rounded-md text-white capitalize ${role.payment === 'unpaid' ? 'bg-gray-500' : 'bg-green-500'}`}>
              {role.paymentTotal === null ? role.payment : <>Approx. Total Pay: <b>{role.paymentTotal}</b></>}
            </span>
            <span className="font-medium capitalize">{role.name}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

const ProjectGroupRow = ({ result }) => {
  const { project, roles } = result
  const [showAll, setShowAll] = useState(false)

  return (
    <li className={`p-2 bg-${project.bgColor} dark:bg-gray-800 bg-opacity-10 dark:bg-opacity-100 rounded border-${project.bgColor} border-l-4`}>
      <div className='flex flex-col sm:flex-row items-center sm:items-start gap-x-3'>
        <div className="relative m-1">
          <div className="w-96 sm:w-52 aspect-w-16 aspect-h-9">
            <img className={`shadow-lg rounded-lg ring-4 ring-${project.bgColor}`} src={project.imageUrl} alt={project.name} />
          </div>
          <span className={`absolute -bottom-1 -right-1 bg-${project.bgColor} rounded-tl px-0.5 py-px`}>
            <FilmIcon className="h-4 w-4 text-white" aria-hidden="true" />
          </span>
        </div>
        <div className='flex flex-col w-96 sm:w-full'>
          {project.featured && <div><span className='px-2 text-xs rounded-md text-white bg-gradient-to-r from-cccpurple via-cccblue to-cccorange'>Featured</span></div>}
          <a target="_blank" className="text-xl capitalize" href={`/projects/${project.slug}`} rel="noreferrer">{project.name}</a>
          <div className='flex gap-x-1 items-center text-xs text-gray-400 dark:text-gray-500'>
            <span>listed by</span> <a className="text-sm capitalize" href={`/m/${project.username}`}>{project.displayName}</a>
          </div>
          <div className='flex gap-x-3'>
            <time className='uppercase text-xs dark:text-gray-300 text-gray-500 flex items-center' dateTime={project.listedAt}>Listed: {project.listedAtInWords}</time>
            <time className='uppercase text-xs dark:text-gray-300 text-gray-500 flex items-center' dateTime={project.deadline}>Deadline: {project.deadlineInWords}</time>
            <div className='uppercase text-xs dark:text-gray-300 text-gray-500 flex items-center'>{project.classification}</div>
            <div className='uppercase text-xs dark:text-gray-300 text-gray-500 flex items-center'>{project.category}</div>
          </div>
          <div className='flex flex-row justify-start items-center gap-x-3 my-1 font-medium text-sm text-gray-700 dark:text-gray-200'>
            <div className='flex items-center space-x-1'>
              <SVGIcon name='icon-manage-projects' className='h-5 w-5' />
              <span>{project.rolesCount}</span>
            </div>
            <div className='flex items-center space-x-1'>
              <SVGIcon name='icon-submissions' className='h-5 w-5' />
              <span>{project.auditionsCount}</span>
            </div>
            <div className='flex items-center space-x-1'>
              <SVGIcon name='icon-followers' className='h-5 w-5' />
              <span>{project.followeesCount}</span>
            </div>
          </div>
          <a target="_blank" href={`/projects/${project.slug}`} className='w-fit px-4 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 hover:bg-gray-50' rel="noreferrer">View Project</a>
        </div>
      </div>

      <div className='mt-2'>
        {roles.map((role, idx) => {
          if (!showAll && idx > 2) return null
          return <ProjectRoleRow key={`role${role.id}`} role={role} bgColor={project.bgColor} />
        })}
        {!showAll && roles.length > 3 && (
          <div onClick={() => setShowAll(true)} className='cursor-pointer text-cccblue text-sm px-4'>
            Show {roles.length - 3} more role(s)
          </div>
        )}
      </div>
    </li>
  )
}

// --- Credit Row ---

const CreditRow = ({ credit }) => {
  return (
    <li className="bg-gray-50 dark:bg-gray-800 hover:bg-cccblue hover:bg-opacity-5 dark:hover:bg-gray-900 p-2 shadow sm:p-2 sm:rounded-lg">
      <a href={`/credits/${credit.slug}`} className="flex gap-x-2 items-center">
        <img className='h-12 w-12 rounded-sm' src={credit.imageUrl} alt={credit.name} />
        <div className='flex flex-col truncate'>
          <span className="font-medium">{credit.name}</span>
          <span className="text-sm dark:text-gray-300 text-gray-500">{credit.year} {credit.parentName}</span>
        </div>
        <span className="ml-auto text-xs text-slate-500">Credit</span>
      </a>
    </li>
  )
}

// --- Main SearchIndex ---

const SearchIndex = () => {
  const urlParams = new window.URLSearchParams(window.location.search)
  const searchParam = urlParams.get('q') || ''
  const [userResults, setUserResults] = useState([])
  const [projectResults, setProjectResults] = useState([])
  const [creditResults, setCreditResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeType, setActiveType] = useState(null)
  const { getRequest } = useQuery()

  const [fetched, setFetched] = useState(false)

  const fetchAll = () => {
    if (searchParam.trim().length === 0) return
    if (fetched) return

    setLoading(true)
    let pending = 3
    const done = () => { pending -= 1; if (pending === 0) { setLoading(false); setFetched(true) } }

    getRequest('/api/v3/omnisearch/talent', { search: 'is:all', contains: searchParam, page: 1, relevance: true }, (err, jsonData) => {
      if (!err) setUserResults(jsonData.results)
      done()
    })

    getRequest('/api/v3/omnisearch/projects_kick', { search: `co:${searchParam}`, payment: 'all', classification: 'all', order_by: 'magic', page: 1 }, (err, jsonData) => {
      if (!err) setProjectResults(jsonData.results)
      done()
    })

    getRequest('/api/v3/omnisearch/credits', { search: searchParam, limit: 30 }, (err, jsonData) => {
      if (!err) setCreditResults(jsonData.results)
      done()
    })
  }

  useEffect(() => {
    if (searchParam.length > 0) {
      fetchAll()
    }
  }, [])

  const handleTypeFilter = (type) => {
    setActiveType(type)
  }

  const activeFilter = TYPE_FILTERS.find((f) => f.key === activeType)
  const advancedUrl = activeFilter?.advancedUrl ? activeFilter.advancedUrl(searchParam) : null
  const hasResults = activeType === null
    ? (userResults.length > 0 || projectResults.length > 0 || creditResults.length > 0)
    : activeType === 'User' ? userResults.length > 0
    : activeType === 'Project' ? projectResults.length > 0
    : creditResults.length > 0

  const PREVIEW_LIMIT = 5

  return (
    <div className='max-w-6xl w-full'>
      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-2">
          {TYPE_FILTERS.map((filter) => (
            <button
              key={filter.label}
              onClick={() => handleTypeFilter(filter.key)}
              className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cccpurple ${
                activeType === filter.key
                  ? 'bg-cccblue text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {filter.label}
              {filter.key === 'User' && userResults.length > 0 && <span className="ml-1 text-xs opacity-75">({userResults.length})</span>}
              {filter.key === 'Project' && projectResults.length > 0 && <span className="ml-1 text-xs opacity-75">({projectResults.length})</span>}
              {filter.key === 'Credit' && creditResults.length > 0 && <span className="ml-1 text-xs opacity-75">({creditResults.length})</span>}
            </button>
          ))}
        </div>
        {advancedUrl && (
          <a href={advancedUrl} className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-cccpurple via-cccblue to-cccorange hover:from-cccpurple hover:to-cccorange hover:text-white shadow-sm">
            Advanced {activeFilter.label} Search &rarr;
          </a>
        )}
      </div>

      {loading && <Loading />}

      {!loading && !hasResults && searchParam.length > 0 && (
        <p className="text-gray-500 dark:text-gray-400 text-sm">No results found.</p>
      )}

      {/* --- All tab: preview cards --- */}
      {!loading && activeType === null && (<div className="space-y-2">
        {userResults.length > 0 && (
          <Card title={`Members (${userResults.length})`}
            header={<a href={TYPE_FILTERS[1].advancedUrl(searchParam)} className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md text-white bg-gradient-to-r from-cccpurple via-cccblue to-cccorange hover:from-cccpurple hover:to-cccorange hover:text-white shadow-sm">Advanced Members Search &rarr;</a>}
          >
            <ul className="space-y-3">
              {userResults.slice(0, PREVIEW_LIMIT).map((result) => (
                <UserRow key={`u${result.id}`} result={result} />
              ))}
            </ul>
          </Card>
        )}

        {projectResults.length > 0 && (
          <Card title={`Projects (${projectResults.length})`}
            header={<a href={TYPE_FILTERS[2].advancedUrl(searchParam)} className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md text-white bg-gradient-to-r from-cccpurple via-cccblue to-cccorange hover:from-cccpurple hover:to-cccorange hover:text-white shadow-sm">Advanced Projects Search &rarr;</a>}
          >
            <ul className="space-y-3">
              {projectResults.slice(0, PREVIEW_LIMIT).map((result) => (
                <ProjectGroupRow key={`p${result.projectId}`} result={result} />
              ))}
            </ul>
          </Card>
        )}

        {creditResults.length > 0 && (
          <Card title={`Credits (${creditResults.length})`}
            header={<a href={TYPE_FILTERS[3].advancedUrl(searchParam)} className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md text-white bg-gradient-to-r from-cccpurple via-cccblue to-cccorange hover:from-cccpurple hover:to-cccorange hover:text-white shadow-sm">Advanced Credits Search &rarr;</a>}
          >
            <ul className="space-y-3">
              {creditResults.slice(0, PREVIEW_LIMIT).map((credit) => (
                <CreditRow key={`c${credit.id}`} credit={credit} />
              ))}
            </ul>
          </Card>
        )}
      </div>)}

      {/* --- Filtered tabs: full results --- */}
      {!loading && activeType === 'User' && userResults.length > 0 && (
        <ul className="space-y-3">
          {userResults.map((result) => (
            <UserRow key={`u${result.id}`} result={result} />
          ))}
        </ul>
      )}

      {!loading && activeType === 'Project' && projectResults.length > 0 && (
        <ul className="space-y-3">
          {projectResults.map((result) => (
            <ProjectGroupRow key={`p${result.projectId}`} result={result} />
          ))}
        </ul>
      )}

      {!loading && activeType === 'Credit' && creditResults.length > 0 && (
        <ul className="space-y-3">
          {creditResults.map((credit) => (
            <CreditRow key={`c${credit.id}`} credit={credit} />
          ))}
        </ul>
      )}
    </div>
  )
}

export default SearchIndex

SearchIndex.propTypes = {}
